import { useEffect, useState } from "react";
import { Table, Container, Button, Form, Spinner } from "react-bootstrap";
import axios from "axios";
import Cookies from "js-cookie";
import useAuth from "../../../hooks/useAuth";
import BaseModal from "../../../components/BaseModal/BaseModal";
import { RADIUS_API, PIECES } from "../../Radius";

const COLONNES = [
    ["civilite", "Civilité"], ["prenom", "Prénom"], ["nom", "Nom"], ["email", "E-mail"],
    ["telephone", "Téléphone"], ["numero_carte", "N° carte étudiant"], ["encadrant", "Encadrant"],
    ["sujet", "Sujet de recherche"], ["structure_recherche", "Structure de recherche"], ["date_depot", "Date de dépôt"],
];

function formatDate(value) {
    return value ? new Date(value).toLocaleString("fr-FR") : "";
}

function CandidaturesRadius() {
    const { auth } = useAuth();
    const token = auth?.accessToken || Cookies.get("accessToken");
    const headers = { Authorization: `Bearer ${token}` };
    const [candidats, setCandidats] = useState([]);
    const [etat, setEtat] = useState("chargement"); // chargement | ok | erreur
    const [recherche, setRecherche] = useState("");
    const [selected, setSelected] = useState(null);
    const [ouverture, setOuverture] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${RADIUS_API}/`, { headers });
                setCandidats(response.data?.candidats || response.data || []);
                setEtat("ok");
            } catch (error) {
                console.error("Erreur récupération candidatures RADiUS", error);
                setEtat("erreur");
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const ouvrirFichier = async (candidat, champ) => {
        setOuverture(champ);
        try {
            const response = await axios.get(`${RADIUS_API}/fichier/${candidat.id}/${champ}`, {
                headers,
                responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
            window.open(url, "_blank");
        } catch (error) {
            console.error(error);
            alert("Impossible d'ouvrir ce fichier.");
        } finally {
            setOuverture("");
        }
    };

    const exporterCSV = () => {
        const echapper = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const lignes = [COLONNES.map(([, label]) => echapper(label)).join(";")];
        candidats.forEach((c) => {
            lignes.push(COLONNES.map(([key]) => echapper(key === "date_depot" ? formatDate(c[key]) : c[key])).join(";"));
        });
        // BOM pour que Excel affiche correctement les accents
        const blob = new Blob(["﻿" + lignes.join("\n")], { type: "text/csv;charset=utf-8" });
        const lien = document.createElement("a");
        lien.href = URL.createObjectURL(blob);
        lien.download = "candidatures_radius.csv";
        lien.click();
    };

    const filtre = recherche.trim().toLowerCase();
    const affiches = candidats.filter((c) =>
        !filtre || [c.prenom, c.nom, c.email, c.numero_carte, c.sujet].some((v) => (v || "").toLowerCase().includes(filtre))
    );

    return (
        <div className="container mt-3 mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <h5 className="m-0">Appel à candidature RADiUS — {candidats.length} candidature(s)</h5>
                <Button variant="success" disabled={candidats.length === 0} onClick={exporterCSV}>
                    Exporter (Excel / CSV)
                </Button>
            </div>
            <Form.Control className="mb-3" placeholder="Rechercher (nom, prénom, e-mail, n° carte, sujet)"
                value={recherche} onChange={(e) => setRecherche(e.target.value)} />

            {etat === "chargement" && <Container className="my-5 text-center"><Spinner animation="border" /></Container>}
            {etat === "erreur" && <Container className="my-5 text-center"><h5>Impossible de récupérer les candidatures.</h5></Container>}
            {etat === "ok" && (affiches.length === 0 ? (
                <Container className="my-5 text-center"><h5>Aucune candidature pour le moment.</h5></Container>
            ) : (
                <Table striped bordered hover responsive className="align-middle">
                    <thead className="bg-light">
                        <tr>
                            <th>#</th>
                            <th>Prénom</th>
                            <th>Nom</th>
                            <th>E-mail</th>
                            <th>Téléphone</th>
                            <th>Sujet</th>
                            <th>Date de dépôt</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {affiches.map((c, i) => (
                            <tr key={c.id ?? c.email}>
                                <td>{i + 1}</td>
                                <td>{c.prenom}</td>
                                <td>{c.nom}</td>
                                <td>{c.email}</td>
                                <td>{c.telephone}</td>
                                <td>{c.sujet}</td>
                                <td>{formatDate(c.date_depot)}</td>
                                <td>
                                    <button className="btn btn-outline-primary"
                                        style={{ color: "#993938", borderColor: "#993938" }}
                                        onClick={() => setSelected(c)}>
                                        consulter
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ))}

            <BaseModal show={!!selected} size={"lg"} handleClose={() => setSelected(null)} title={"Candidature RADiUS"}>
                {selected && (
                    <>
                        <Table bordered size="sm">
                            <tbody>
                                {COLONNES.map(([key, label]) => (
                                    <tr key={key}>
                                        <th style={{ width: "35%" }}>{label}</th>
                                        <td>{key === "date_depot" ? formatDate(selected[key]) : selected[key]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        <p className="fw-bold mb-2">Pièces jointes</p>
                        {PIECES.map((p) => (
                            <div key={p.name} className="d-flex justify-content-between align-items-center border-bottom py-1">
                                <span className="me-2">{p.label}</span>
                                {selected[p.name] ? (
                                    <Button size="sm" variant="outline-primary" disabled={ouverture === p.name}
                                        onClick={() => ouvrirFichier(selected, p.name)}>
                                        {ouverture === p.name ? <Spinner size="sm" animation="border" /> : "Ouvrir"}
                                    </Button>
                                ) : <span className="text-muted">non fourni</span>}
                            </div>
                        ))}
                    </>
                )}
            </BaseModal>
        </div>
    );
}

export default CandidaturesRadius;
